// Validation for the daily-tracking forms. Ranges catch typos; they are not clinical advice.
import { z } from 'zod';
import { optionalNumber, optionalText } from '../../lib/forms';
import { fromLocalInput } from '../../lib/time';
import { checkBloodPressure, diastolic, pulse, systolic, weightKg } from '../sessions/fields';

/** A datetime-local value that isn't in the future (a few minutes of clock slack allowed). */
export const entryTime = z
  .string()
  .refine((value) => fromLocalInput(value) != null, 'Choose a date and time.')
  .refine((value) => (fromLocalInput(value) ?? '') <= new Date(Date.now() + 5 * 60_000).toISOString(), 'Choose a time that isn’t in the future.')
  .transform((value) => fromLocalInput(value)!);

const required = (schema: ReturnType<typeof optionalNumber>, message: string) => schema.refine((value): value is number => value != null, message);

export const fluidSchema = z.object({
  taken_at: entryTime,
  amount_ml: required(optionalNumber(1, 3000, 'Enter an amount between 1 and 3000 mL.'), 'Enter how much.'),
  kind: z.enum(['water', 'tea', 'soup', 'other']),
  note: optionalText(200)
});

export const weightSchema = z.object({
  measured_at: entryTime,
  weight_kg: required(weightKg, 'Enter your weight.'),
  context: z.enum(['morning', 'other'])
});

export const bpSchema = z
  .object({
    measured_at: entryTime,
    bp_systolic: systolic,
    bp_diastolic: diastolic,
    pulse
  })
  .superRefine(checkBloodPressure)
  .refine((values) => values.bp_systolic != null, { message: 'Enter both blood pressure numbers.', path: ['bp_systolic'] });

export const symptomSchema = z.object({
  occurred_at: entryTime,
  symptom: z.string().trim().min(1, 'Choose or type a symptom.').max(60, 'Keep it under 60 characters.'),
  severity: z.number().int().min(1).max(5),
  during_session: z.boolean(),
  note: optionalText(500)
});

const sign = z.enum(['yes', 'no', '']).transform((value) => (value === 'yes' ? 1 : value === 'no' ? 0 : null));

export const accessSchema = z.object({
  checked_at: entryTime,
  access_type: z.enum(['avf', 'avg', 'catheter', '']).transform((value) => (value === '' ? null : value)),
  thrill_present: sign,
  bruit_present: sign,
  redness: sign,
  swelling: sign,
  pain: sign,
  bleeding_prolonged: sign,
  discharge: sign,
  fever: sign,
  note: optionalText(500)
});

/** Form value for a stored 0/1/null sign. */
export const signValue = (value: number | null | undefined): 'yes' | 'no' | '' => (value === 1 ? 'yes' : value === 0 ? 'no' : '');
